import localforage from "localforage";
import { computed } from "vue";
import { getEnvConfig } from "~/services/appConfig";
import { authStore } from "~/stores/authStore";
import { apiClient } from "./apiClient";

/**
 *
 * @param value - number or string
 * @param showZero - if true, will return 0 if value is null or undefined
 * @returns
 */
export function formatNegNumber(
  value: number | string | undefined | null,
  showZero: boolean | string,
) {
  return formatNumber(value, true, showZero, true);
}

/**
 *
 * @param value
 * @param showDecimals - default true, false for no decimals, 3 for 3 decimals
 * @param showZero - defaults to false
 * @param negInBrackets - defaults to false
 * @returns
 */
export function formatNumber(
  value: number | string | undefined | null,
  showDecimals: boolean | number = true,
  showZero: boolean | string = false,
  negInBrackets = false,
): string {
  if (!value) {
    if (!showZero) {
      return "";
    }
    if (typeof showZero === "string") {
      return showZero;
    }
  }

  let numDecimals = showDecimals;
  const num = Number(value);
  if (numDecimals === -2) {
    if (Math.floor(num) === num) {
      numDecimals = 0;
    } else {
      numDecimals = 2;
    }
  }
  let result: string;
  if (numDecimals === true || numDecimals === 2) {
    result = formatter2.format(num).trim();
  } else if (numDecimals === 3) {
    result = formatter3.format(num).trim();
  } else {
    result = formatter0.format(num).trim();
  }

  if (num < 0 && negInBrackets) {
    result = `(${result.replace("-", "")})`;
  }
  return result;
}

const formatter3 = new Intl.NumberFormat("en-CA", {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});
const formatter2 = new Intl.NumberFormat("en-CA", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatter0 = new Intl.NumberFormat("en-CA", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Get a summary of the comments.
 * @param comments
 * @returns
 */
export function plural(count: number, singular?: string, pluralStr?: string) {
  return count === 1 ? (singular ?? "") : (pluralStr ?? "s");
}

/**
 * This method capitalizes the keys of an object.
 * @param obj object to capitalize keys
 * @returns the object with capitalized keys
 */
export function capitalizeKeys(obj: any) {
  console.log("capitalizeKeys", obj);
  const capitalizedObj: any = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      capitalizedObj[capitalizedKey] = obj[key];
    }
  }
  return capitalizedObj;
}

// capitalizes the first letter each work in a string
export function capitalizeWords(s: string) {
  return s.replaceAll(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Split a string by capital letters or digits.
 * @param s string to split: Eg. 'ThisIsAString'
 * @returns The string split by capital letters: Eg. 'This Is A String'
 */
export function splitByCaps(s: string | undefined | null, space = " ") {
  return (
    (s ?? "")
      .match(/[A-Z]+(?![a-z])|[A-Z]?[a-z]+|\d+/g)
      ?.join(space)
      .replace("_", " ") ?? ""
  );
}

/**
 * Get the number of days since a date.
 * @param date
 * @returns
 */
export function daysOld(date: Date | string | null | undefined) {
  if (!date) {
    return null;
  }

  const diff = new Date().getTime() - new Date(date).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Get the time from a date object in 12 hour format.
 * @param date the date object to get the time from
 * @returns the time in 12 hour format
 */
export function getTime(date: Date | string | null | undefined) {
  if (!date) {
    return null;
  }
  return new Date(date).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export async function refreshCaches() {
  // need to remember the authStoreType before clearing localforage, and set it again after
  const authStoreType = authStore.authStoreType;
  await Promise.all([localforage.clear(), apiClient.userInfo.forgetAboutMe()]);
  if (authStoreType) {
    await localforage.setItem(authStore.KeyForLocalForage, authStoreType);
  }
}

export async function refreshNamedCaches(namesToRefresh: string | undefined) {
  if (!namesToRefresh) {
    return;
  }
  console.log("refreshNamedCaches", namesToRefresh);
  // clear the named caches... will be reloaded when needed
  const namesList = namesToRefresh.split(",");
  namesList.forEach(async (name) => {
    await localforage.removeItem(`${name}Store`);
  });
}

/**
 * Compares array of objects
 * @param arr1 First array
 * @param arr2 Second array
 * @returns True if the arrays are identical
 */
export const arraysAreIdentical = (
  arr1: any[] | undefined | null,
  arr2: any[] | undefined | null,
) => {
  if (!arr1 || !arr2) {
    return arr1 === arr2;
  }
  if (arr1.length !== arr2.length) {
    return false;
  }

  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  return set1.size === set2.size && [...set1].every((item) => set2.has(item));
};

export const getMessageTime = (msg: string) => {
  // default to 3 seconds + a second per 20 characters
  return 3000 + Math.floor(msg.length / 20) * 1000;
};

type BuildIdentity = {
  commit: string;
  buildTime: string;
};

function getWebBuildIdentity(): BuildIdentity {
  return {
    commit: process.env.COMMIT_HASH ?? "",
    buildTime: process.env.BUILD_TIME ?? "",
  };
}

function getServerBuildIdentity(): BuildIdentity {
  try {
    const env = getEnvConfig();
    return {
      commit: env.serverCommit ?? "",
      buildTime: env.serverBuildTime ?? "",
    };
  } catch {
    // Config not loaded yet (should be rare after main.ts awaits loadEnvConfig)
    return { commit: "", buildTime: "" };
  }
}

function formatSingleBuild(
  label: string | null,
  identity: BuildIdentity,
): string {
  const commit = identity.commit.trim();
  const buildTime = identity.buildTime.trim();
  if (!commit && !buildTime) {
    return "";
  }
  if (!commit) {
    // Build time only (e.g. server build-info without a usable git commit)
    return label ? `${label}: built ${buildTime}` : `Built: ${buildTime}`;
  }
  if (label) {
    return buildTime
      ? `${label}: ${commit} (${buildTime})`
      : `${label}: ${commit}`;
  }
  return buildTime
    ? `Commit: ${commit} - Built: ${buildTime}`
    : `Commit: ${commit}`;
}

/**
 * Combines frontend (Vite-baked) and server (/envConfig) build identity.
 * When commits match, shows a single line; when they differ, shows both so
 * independent Web vs Server deploys are visible. Either side may contribute
 * build time without a commit.
 */
export function formatCommitBuildInfo(): string {
  const web = getWebBuildIdentity();
  const server = getServerBuildIdentity();

  const webCommit = web.commit.trim();
  const serverCommit = server.commit.trim();
  const hasWeb = !!(webCommit || web.buildTime.trim());
  const hasServer = !!(serverCommit || server.buildTime.trim());

  if (hasWeb && hasServer) {
    if (webCommit && serverCommit && webCommit === serverCommit) {
      // Prefer the more complete single-line format; use web build time if present
      const built = web.buildTime.trim() || server.buildTime.trim();
      return built
        ? `Commit: ${webCommit} - Built: ${built}`
        : `Commit: ${webCommit}`;
    }
    const webPart = formatSingleBuild("Web", web);
    const serverPart = formatSingleBuild("Server", server);
    if (webPart && serverPart) {
      return `${webPart} | ${serverPart}`;
    }
    return webPart || serverPart;
  }

  if (hasWeb) {
    return formatSingleBuild(null, web);
  }

  if (hasServer) {
    return formatSingleBuild("Server", server);
  }

  return "";
}

export const commitBuildInfo = computed(() => formatCommitBuildInfo());

// for display in the top bar, we want to show the branch name in dev, and the environment in non-dev
// only displayed in layout pages if not "live"
export function getModeDisplay() {
  const buildInfo = formatCommitBuildInfo();
  if (import.meta.env.DEV) {
    return buildInfo
      ? `Development - "${process.env.BRANCH_NAME}" - ${buildInfo}`
      : `Development - "${process.env.BRANCH_NAME}"`;
  }
  const envMode = getEnvConfig().env;
  return buildInfo
    ? `${capitalizeWords(envMode)} Environment - ${buildInfo}`
    : `${capitalizeWords(envMode)} Environment`;
}

// for the browser title, we want it to be shorter and just show the branch name in dev, and nothing in live
export function getModeForTitle() {
  if (import.meta.env.DEV) {
    return ` Dev (${process.env.BRANCH_NAME})`;
  }
  const envMode = getEnvConfig().env;
  if (envMode === "live") {
    return "";
  }
  return ` ${capitalizeWords(envMode)}`;
}

/** Short prefix of a GUID for compact inline display (full value in title/tooltip). */
export function truncateGuid(guid?: string | null, length = 7): string {
  if (!guid) {
    return "";
  }
  return guid.slice(0, length);
}
